import React, { useEffect, useState } from 'react';
import { useAuth } from '../../../commons/Auth';
import { useAlert } from 'react-alert';
import { useLoading } from '../../../commons/Loading';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { getSignedUrl, getYousignFile } from '../../../lib/fetch/files';
import { deleteSuppAdminAgreement } from '../../../lib/fetch/agreements';
import SupplierAdminAgreementsPresentational from './SupplierAdminAgreementsPresentational';
import { Redirect } from 'react-router-dom';

const SupplierAdminAgreements = (props) => {
  const [t] = useTranslation('CONTRACTS_AREA');
  const [{ newToken, profile = {} } = {}] = useAuth();
  const alert = useAlert();
  const { showLoading, hideLoading } = useLoading();

  // Stepper 1
  const [activities, setActivities] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [agreementTypes, setAgreementTypes] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [selectedActivityId, setSelectedActivityId] = useState(null);

  const [selectedSupplier, setSelectedSupplier] = useState(null);

  // Stepper 2
  const [documents, setDocuments] = useState([]);
  const [totalDocuments, setTotalDocuments] = useState(0);
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [selectedAll, setSelectedAll] = useState(false);
  const [requiredDocuments, setRequiredDocuments] = useState([]);
  const [missDocumentsRequired, setMissDocumentsRequired] = useState(false);
  const [documentsForRequired, setDocumentsForRequired] = useState([]);

  const [redirectTo, setRedirectTo] = useState(null);

  // Parametri Legati ai Documenti
  const [queryDocuments, setQueryDocuments] = useState({
    limitDocuments: 10,
    offsetDocuments: 0,
  });
  const { limitDocuments, offsetDocuments } = queryDocuments;

  function onFilterDocuments(queryDiff) {
    if (
      queryDiff.limitDocuments &&
      queryDiff.limitDocuments !== limitDocuments
    ) {
      queryDiff.offsetDocuments = 0;
    }
    setQueryDocuments(queryDiff);
  }

  // Fetch attivitá per Select Attivitá
  const fetchActivities = async () => {
    showLoading();

    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/activities`,
      {
        headers: { Authorization: newToken },
        params: {
          limit: 9999,
          offset: 0,
        },
      }
    );

    hideLoading();

    if (response instanceof Error) {
      return alert.error(t('COMMON:GENERIC_ERROR'));
    }

    const { data = [] } = response;

    setActivities(data.activities);
  };

  // Fetch attivitá per Select Attivitá
  const fetchDocumentTypes = async () => {
    showLoading();

    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/document-types`,
      {
        headers: { Authorization: newToken },
        params: {
          limit: 9999,
          offset: 0,
        },
      }
    );

    hideLoading();

    if (response instanceof Error) {
      return alert.error(t('COMMON:GENERIC_ERROR'));
    }

    const { data = [] } = response;

    setDocumentTypes(data.documentTypes);
  };

  // Fetch attivitá per Select Attivitá
  const fetchAgreementTypes = async (activityId) => {
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/agreement-types`,
      {
        headers: { Authorization: newToken },
        params: {
          limit: 9999,
          offset: 0,
          activityId,
          userType: 'ADMIN',
        },
      }
    );

    if (response instanceof Error) {
      return alert.error(t('COMMON:GENERIC_ERROR'));
    }

    const { data = [] } = response;

    setAgreementTypes(data.agreementTypes);
  };

  // Fetch Fornitori per Select Fornitori
  const fetchSuppliers = async (activityId) => {
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/agreements/suppliers`,
      {
        headers: { Authorization: newToken },
        params: { activityId },
      }
    );

    if (response instanceof Error) {
      return alert.error(t('COMMON:GENERIC_ERROR'));
    }

    const { data = [] } = response;

    setSuppliers(data.suppliers);
  };

  async function fetchAllRequiredDocuments() {
    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/documents/for-agreements/${profile._id}`,
      {
        limit: 9999,
        offset: 0,
        needed: true,
        forAgreements: true,
        selectedSupplier,
        requiredDocuments,
        selectedActivityId,
      },
      {
        headers: { Authorization: newToken },
      }
    );

    if (response instanceof Error) {
      return [];
    }

    const { data = [] } = response;

    setDocumentsForRequired(data.documents);
    return data.documents;
  }

  // Fetch Documenti per lo stepper (con paginazione)
  async function fetchDocuments() {
    showLoading();

    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/documents/for-agreements/${profile._id}`,
      {
        limit: limitDocuments,
        offset: offsetDocuments,
        needed: true,
        forAgreements: true,
        selectedSupplier,
        requiredDocuments,
        selectedActivityId,
      },
      {
        headers: { Authorization: newToken },
      }
    );

    hideLoading();

    if (response instanceof Error) {
      return alert.error(t('COMMON:GENERIC_ERROR'));
    }

    const { data = [], headers } = response;

    setDocuments(data.documents);
    setTotalDocuments(+headers['x-total-count']);
  }

  const onSelectChange = (_id, typeId) => {
    if (selectedDocuments.find((value) => value._id === _id)) {
      setSelectedAll(false);
      return setSelectedDocuments((selectedDocuments) =>
        selectedDocuments.filter((value) => value._id !== _id)
      );
    }
    setSelectedDocuments((selectedDocuments) => [
      ...selectedDocuments,
      { _id, typeId },
    ]);
  };

  const onSelectAll = (selectAll) => {
    setSelectedAll(selectAll);
    if (!selectAll) {
      return setSelectedDocuments(
        documents
          .filter(
            (document) =>
              document.type?.required === true && document._id !== undefined
          )
          .map(({ _id, typeId }) => ({ _id, typeId }))
      );
    }
    setSelectedDocuments(
      documents
        .filter((document) => document._id !== undefined)
        .map(({ _id, typeId }) => {
          return { _id, typeId };
        })
    );
  };

  async function handleFileListView(key) {
    showLoading();

    const { data, error } = await getSignedUrl(key, newToken);

    if (error) {
      hideLoading();
      alert.error(t('COMMON:GENERIC_ERROR'));
      return;
    }

    hideLoading();
    window.open(data.url, '_blank');
  }

  async function handleDelete({ agreementId, key }) {
    showLoading();

    const { error, data } = await deleteSuppAdminAgreement({
      agreementId,
      key,
      newToken,
    });

    if (error) {
      hideLoading();

      alert.error(t('COMMON:GENERIC_ERROR'));
      return;
    }

    hideLoading();
    alert.success(t('COMMON:SUCCESS_DELETE'));
  }

  async function handleFileSigned(signingRequestId) {
    showLoading();

    const { data, error, headers } = await getYousignFile(signingRequestId);

    if (error) {
      hideLoading();
      alert.error(t('COMMON:GENERIC_ERROR'));
      return;
    }
    hideLoading();

    const file = new Blob([data], { type: headers['content-type'] });
    const fileURL = URL.createObjectURL(file);
    window.open(fileURL, '_blank');
  }

  useEffect(() => {
    fetchActivities();
    fetchDocumentTypes();
  }, []);

  useEffect(() => {
    if (selectedSupplier) {
      fetchDocuments();
    }
  }, [limitDocuments, offsetDocuments, selectedSupplier, selectedActivityId]);

  useEffect(() => {
    if (selectedActivityId && selectedSupplier) {
      fetchAllRequiredDocuments();
    }
  }, [selectedSupplier, selectedActivityId, requiredDocuments]);

  if (redirectTo) {
    return <Redirect to={redirectTo} />;
  }

  return (
    <SupplierAdminAgreementsPresentational
      activities={activities}
      documentTypes={documentTypes}
      agreementTypes={agreementTypes}
      suppliers={suppliers}
      fetchSuppliers={fetchSuppliers}
      fetchAgreementTypes={fetchAgreementTypes}
      documents={documents}
      totalDocuments={totalDocuments}
      limitDocuments={limitDocuments}
      offsetDocuments={offsetDocuments}
      onFilterDocuments={onFilterDocuments}
      onSelectChange={onSelectChange}
      onSelectAll={onSelectAll}
      selectedAll={selectedAll}
      selectedDocuments={selectedDocuments}
      handleFileSigned={handleFileSigned}
      handleFileListView={handleFileListView}
      handleDelete={handleDelete}
      fetchDocuments={fetchDocuments}
      setSelectedSupplier={setSelectedSupplier}
      setRedirectTo={setRedirectTo}
      setRequiredDocuments={setRequiredDocuments}
      setSelectedActivityId={setSelectedActivityId}
      missDocumentsRequired={missDocumentsRequired}
      documentsForRequired={documentsForRequired}
      {...props}
    />
  );
};

export default SupplierAdminAgreements;
